import React, { useState, useEffect, useRef } from 'react';
import flatpickr from 'flatpickr';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Preloader from '../components/Preloader';
import ScrollToTop from '../components/ScrollToTop';
import PageTitle from '../components/PageTitle';
import { BRAND, whatsappLink } from '../data/brand';
import { roomOptions } from '../data/rooms';

export default function Contact() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    roomType: '',
    checkIn: '',
    checkOut: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const ciRef = useRef(null);
  const coRef = useRef(null);

  useEffect(() => {
    const fi = flatpickr(ciRef.current, {
      dateFormat: 'Y-m-d',
      minDate: 'today',
    });
    const fo = flatpickr(coRef.current, {
      dateFormat: 'Y-m-d',
      minDate: 'today',
    });
    return () => {
      fi.destroy();
      fo.destroy();
    };
  }, []);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Required';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email))
      e.email = 'Valid email required';
    if (!form.message.trim()) e.message = 'Required';
    return e;
  };

  const h = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: '' });
  };

  const submit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setBusy(true);
    setTimeout(() => {
      setBusy(false);
      setOk(true);
      setForm({
        name: '',
        email: '',
        phone: '',
        roomType: '',
        checkIn: '',
        checkOut: '',
        message: '',
      });
      setTimeout(() => setOk(false), 7000);
    }, 1000);
  };

  const field = (name, label, placeholder, type = 'text', required = false) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 1.5,
          textTransform: 'uppercase',
          color: '#555',
        }}
      >
        {label}
        {required && ' *'}
      </label>
      <input
        name={name}
        type={type}
        value={form[name]}
        onChange={h}
        placeholder={placeholder}
        style={{
          padding: '11px 14px',
          border: `1.5px solid ${errors[name] ? '#dc3545' : '#e8e3dc'}`,
          borderRadius: 3,
          fontSize: 14,
          fontFamily: 'var(--mv-font-body,sans-serif)',
          outline: 'none',
          color: '#111',
          background: '#fff',
          transition: 'border-color 0.2s',
        }}
        onFocus={(e) => (e.target.style.borderColor = '#b08d57')}
        onBlur={(e) =>
          (e.target.style.borderColor = errors[name] ? '#dc3545' : '#e8e3dc')
        }
      />
      {errors[name] && (
        <span style={{ fontSize: 12, color: '#dc3545' }}>{errors[name]}</span>
      )}
    </div>
  );

  return (
    <div className="page-wrapper">
      <Preloader />
      <Header />
      <PageTitle
        title="Contact & Reservations"
        breadcrumbs={[{ label: 'Contact' }]}
      />

      <section style={{ padding: '64px 0' }}>
        <div className="auto-container">
          <div className="row g-5">
            {/* Left — info */}
            <div className="col-lg-4">
              <span className="h2-label">Find Us</span>
              <h2
                style={{
                  fontFamily: 'var(--gw-font-h,serif)',
                  fontSize: 'clamp(24px,3vw,40px)',
                  color: '#111',
                  lineHeight: 1.15,
                  margin: '10px 0 18px',
                }}
              >
                {BRAND.name}
              </h2>

              {/* Address */}
              <div style={{ marginBottom: 20 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 1.5,
                    textTransform: 'uppercase',
                    color: '#999',
                    marginBottom: 6,
                  }}
                >
                  Address
                </div>
                <p
                  style={{
                    fontSize: 14,
                    color: '#444',
                    lineHeight: 1.7,
                    margin: 0,
                  }}
                >
                  {BRAND.addressLines.map((l, i) => (
                    <React.Fragment key={i}>
                      {l}
                      <br />
                    </React.Fragment>
                  ))}
                </p>
              </div>

              {/* Phones */}
              <div style={{ marginBottom: 20 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 1.5,
                    textTransform: 'uppercase',
                    color: '#999',
                    marginBottom: 8,
                  }}
                >
                  Phone Numbers
                </div>
                <div
                  style={{ display: 'flex', flexDirection: 'column', gap: 5 }}
                >
                  {BRAND.phones.map((p) => (
                    <a
                      key={p.tel}
                      href={'tel:' + p.tel}
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: '#111',
                        textDecoration: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      <i
                        className="fas fa-phone"
                        style={{ color: '#b08d57', fontSize: 12, width: 14 }}
                      ></i>
                      {p.label}
                    </a>
                  ))}
                </div>
              </div>

              {/* WhatsApp */}
              <div style={{ marginBottom: 20 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 1.5,
                    textTransform: 'uppercase',
                    color: '#999',
                    marginBottom: 8,
                  }}
                >
                  WhatsApp
                </div>
                <a
                  href={whatsappLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '10px 18px',
                    background: '#25d366',
                    color: '#fff',
                    borderRadius: 3,
                    fontSize: 13,
                    fontWeight: 700,
                    textDecoration: 'none',
                  }}
                >
                  <i className="fab fa-whatsapp"></i>
                  {BRAND.whatsappLabel}
                </a>
              </div>

              {/* Email */}
              <div style={{ marginBottom: 20 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 1.5,
                    textTransform: 'uppercase',
                    color: '#999',
                    marginBottom: 6,
                  }}
                >
                  Email
                </div>
                <a
                  href={`mailto:${BRAND.email}`}
                  style={{
                    fontSize: 13,
                    color: '#b08d57',
                    textDecoration: 'none',
                  }}
                >
                  {BRAND.email}
                </a>
              </div>

              {/* Website */}
              <div style={{ marginBottom: 24 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 1.5,
                    textTransform: 'uppercase',
                    color: '#999',
                    marginBottom: 6,
                  }}
                >
                  Website
                </div>
                <a
                  href={BRAND.websiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    fontSize: 13,
                    color: '#b08d57',
                    textDecoration: 'none',
                  }}
                >
                  {BRAND.website}
                </a>
              </div>

              {/* Hours */}
              <div
                style={{
                  background: '#f9f8f6',
                  border: '1px solid #e8e3dc',
                  borderLeft: '4px solid #b08d57',
                  borderRadius: 3,
                  padding: '14px 16px',
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 1.5,
                    textTransform: 'uppercase',
                    color: '#999',
                    marginBottom: 4,
                  }}
                >
                  Reception
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#111' }}>
                  Open 24 Hours — 7 Days a Week
                </div>
              </div>
            </div>

            {/* Right — form */}
            <div className="col-lg-8">
              <div
                style={{
                  background: '#f9f8f6',
                  borderRadius: 8,
                  padding: '40px 40px',
                  border: '1px solid #e8e3dc',
                }}
              >
                <span className="h2-label">Reservation Form</span>
                <h3
                  style={{
                    fontFamily: 'var(--gw-font-h,serif)',
                    fontSize: 'clamp(22px,2.5vw,34px)',
                    color: '#111',
                    margin: '8px 0 24px',
                  }}
                >
                  Book Your Stay
                </h3>

                {ok && (
                  <div
                    style={{
                      background: '#f0faf4',
                      border: '1px solid #27ae60',
                      color: '#27ae60',
                      padding: '12px 16px',
                      borderRadius: 4,
                      marginBottom: 20,
                      fontSize: 14,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                    }}
                  >
                    <i className="fas fa-circle-check"></i> Thank you! We'll
                    contact you shortly to confirm your reservation.
                  </div>
                )}

                <form onSubmit={submit} noValidate>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 14,
                      marginBottom: 14,
                    }}
                  >
                    {field(
                      'name',
                      'Full Name',
                      'e.g. John Adeyemi',
                      'text',
                      true,
                    )}
                    {field(
                      'email',
                      'Email Address',
                      'your@email.com',
                      'email',
                      true,
                    )}
                    {field('phone', 'Phone Number', '0809 xxx xxxx')}
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 5,
                      }}
                    >
                      <label
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          letterSpacing: 1.5,
                          textTransform: 'uppercase',
                          color: '#555',
                        }}
                      >
                        Room Type
                      </label>
                      <select
                        name="roomType"
                        value={form.roomType}
                        onChange={h}
                        style={{
                          padding: '11px 14px',
                          border: '1.5px solid #e8e3dc',
                          borderRadius: 3,
                          fontSize: 14,
                          fontFamily: 'var(--mv-font-body,sans-serif)',
                          outline: 'none',
                          color: '#111',
                          background: '#fff',
                        }}
                      >
                        <option value="">Select room type…</option>
                        {roomOptions.map((label, i) => (
                          <option key={i}>{label}</option>
                        ))}
                        <option>Event / Conference Hall</option>
                        <option>Boardroom</option>
                        <option>General Enquiry</option>
                      </select>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 5,
                      }}
                    >
                      <label
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          letterSpacing: 1.5,
                          textTransform: 'uppercase',
                          color: '#555',
                        }}
                      >
                        Check-In Date
                      </label>
                      <input
                        ref={ciRef}
                        name="checkIn"
                        placeholder="Select date"
                        readOnly
                        style={{
                          padding: '11px 14px',
                          border: '1.5px solid #e8e3dc',
                          borderRadius: 3,
                          fontSize: 14,
                          background: '#fff',
                          cursor: 'pointer',
                          outline: 'none',
                          color: '#111',
                        }}
                      />
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 5,
                      }}
                    >
                      <label
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          letterSpacing: 1.5,
                          textTransform: 'uppercase',
                          color: '#555',
                        }}
                      >
                        Check-Out Date
                      </label>
                      <input
                        ref={coRef}
                        name="checkOut"
                        placeholder="Select date"
                        readOnly
                        style={{
                          padding: '11px 14px',
                          border: '1.5px solid #e8e3dc',
                          borderRadius: 3,
                          fontSize: 14,
                          background: '#fff',
                          cursor: 'pointer',
                          outline: 'none',
                          color: '#111',
                        }}
                      />
                    </div>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 5,
                      marginBottom: 18,
                    }}
                  >
                    <label
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: 1.5,
                        textTransform: 'uppercase',
                        color: '#555',
                      }}
                    >
                      Message / Special Requests *
                    </label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={h}
                      rows="4"
                      placeholder="Tell us about your stay or any special arrangements…"
                      style={{
                        padding: '11px 14px',
                        border: `1.5px solid ${errors.message ? '#dc3545' : '#e8e3dc'}`,
                        borderRadius: 3,
                        fontSize: 14,
                        fontFamily: 'var(--mv-font-body,sans-serif)',
                        resize: 'vertical',
                        outline: 'none',
                        color: '#111',
                        background: '#fff',
                        minHeight: 100,
                      }}
                    ></textarea>
                    {errors.message && (
                      <span style={{ fontSize: 12, color: '#dc3545' }}>
                        {errors.message}
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 14,
                      flexWrap: 'wrap',
                    }}
                  >
                    <button
                      type="submit"
                      className="h2-btn-primary"
                      disabled={busy}
                      style={{ opacity: busy ? 0.7 : 1 }}
                    >
                      {busy ? 'Sending…' : 'Send Reservation Request'}
                    </button>
                    <span style={{ fontSize: 13, color: '#999' }}>
                      or call{' '}
                      <a
                        href={`tel:${BRAND.phones[0].tel}`}
                        style={{ color: '#b08d57', fontWeight: 700 }}
                      >
                        {BRAND.phones[0].label}
                      </a>
                    </span>
                  </div>
                </form>
              </div>

              {/* Map */}
              <div
                style={{
                  marginTop: 20,
                  borderRadius: 8,
                  overflow: 'hidden',
                  border: '1px solid #e8e3dc',
                }}
              >
                <iframe
                  title={`${BRAND.name} Map`}
                  src="https://maps.google.com/maps?width=100%25&height=300&hl=en&q=Jacob Zuma Road+Imo&t=&z=13&ie=UTF8&iwloc=B&output=embed"
                  style={{
                    width: '100%',
                    height: 260,
                    border: 0,
                    display: 'block',
                  }}
                  allowFullScreen
                  loading="lazy"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <ScrollToTop />
    </div>
  );
}
